import type { NormalizedRemedy } from "@common/types/issue";

export function liveReviewIssueKey(issue: { ruleUuid?: string; index: number }): string {
  return issue.ruleUuid ?? `idx-${issue.index}`;
}

const ANONYMOUS_ACTION_ID = "anon";

export function buildActionId(
  messageId: string | undefined,
  issueId: string,
  kind: "remedy" | "comment",
  subId: string,
): string {
  return `live_review:${messageId ?? ANONYMOUS_ACTION_ID}:${issueId}:${kind}:${subId}`;
}

const LIVE_REVIEW_ACTION_PREFIX = "live_review:";
const BULK_APPLY_MARKER = "bulk-apply";
const LIVE_REVIEW_ACTION_KINDS: ReadonlySet<string> = new Set(["remedy", "comment", "rule"]);

export function toolCallIdFromActionId(actionId: string): string | null {
  if (!actionId.startsWith(LIVE_REVIEW_ACTION_PREFIX)) return actionId;
  const segments = actionId.slice(LIVE_REVIEW_ACTION_PREFIX.length).split(":");
  if (segments[segments.length - 1] === BULK_APPLY_MARKER) return null;
  const [toolCallId, , actionKind] = segments;
  return segments.length >= 4 && LIVE_REVIEW_ACTION_KINDS.has(actionKind) && toolCallId && toolCallId !== ANONYMOUS_ACTION_ID ? toolCallId : null;
}

export function buildConcedeId(
  messageId: string | undefined,
  issueId: string,
): string {
  return `live_review:${messageId ?? ANONYMOUS_ACTION_ID}:${issueId}:rule:concede`;
}

export function buildBulkApplyId(reviewId: string): string {
  return `live_review:${reviewId}:review:${BULK_APPLY_MARKER}`;
}

export function buildBulkApplyRuleId(reviewId: string, ruleUuid: string): string {
  return `live_review:${reviewId}:rule:${ruleUuid}:${BULK_APPLY_MARKER}`;
}

const WORD_NS = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";
const PREVIEW_ONLY_AUTHOR = "Preview";

function escapeXmlText(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeXmlAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/'/g, "&apos;");
}

export function trackedChangesToOoxml(
  changes: Array<{ tag: string; text: string | undefined }>,
  opts: { author: string },
): string {
  if (!changes || changes.length === 0) return "";
  if (!opts.author) {
    throw new Error("Cannot attribute a tracked change: no author name was resolved for this user.");
  }
  const safeAuthor = escapeXmlAttr(opts.author);
  const safeDate = escapeXmlAttr(new Date().toISOString());
  const runs = changes
    .map((c, idx) => {
      const text = escapeXmlText(c.text ?? "");
      if (c.tag === "del") {
        return `<w:del xmlns:w="${WORD_NS}" w:id="${idx}" w:author="${safeAuthor}" w:date="${safeDate}"><w:r><w:delText xml:space="preserve">${text}</w:delText></w:r></w:del>`;
      }
      return `<w:ins xmlns:w="${WORD_NS}" w:id="${idx}" w:author="${safeAuthor}" w:date="${safeDate}"><w:r><w:t xml:space="preserve">${text}</w:t></w:r></w:ins>`;
    })
    .join("");
  return `<w:p xmlns:w="${WORD_NS}">${runs}</w:p>`;
}

export function resolveRemedyOoxml(
  remedy: NormalizedRemedy,
  opts: { author: string },
): string {
  if (remedy.trackedChanges && remedy.trackedChanges.length > 0) {
    return trackedChangesToOoxml(remedy.trackedChanges, opts);
  }
  return (remedy.ooxmlAfter ?? []).join("");
}

export function remedyPreviewOoxml(remedy: NormalizedRemedy): string {
  return resolveRemedyOoxml(remedy, { author: PREVIEW_ONLY_AUTHOR });
}
